import { parsePhoneNumber } from 'libphonenumber-js';
import throttle from 'lodash/throttle';
import React, { useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, Text, TextInput, View } from 'react-native';

import OTPVoiceCallContainer from './OTPVoiceCallContainer';

import {
  useRespondToMyEmailReminderChallengeMutation,
  useInitiateMyEmailReminderMutation,
} from 'apollo/queries/autogenerate/hooks-public';
import {
  PinCodeDispatchMethod,
  Platform as ReCaptchaPlatform,
} from 'apollo/queries/autogenerate/schemas-public';
import { ACTION, ITEM, SCREEN } from 'shared/modules/auth/constants/mixpanel';
import CodeInput from 'shared/modules/common/components/atoms/CodeInput';
import Mixpanel from 'shared/vendor/Mixpanel';

const OTP_IDLE_TIME_LIMIT = 3 * 60 * 1000;
const VIEW_SUCCESS_PAGE = `${SCREEN.EMAIL_REMINDER_SUCCESS} - ${ACTION.PAGE_VIEW}`;
const ERROR_INVALID_OTP = `${SCREEN.FORGOT_EMAIL_INVALID_OTP_ERROR} - ${ACTION.ERROR}`;
const VIEW_FORGOT_EMAIL_TIMEOUT_ERROR = `${SCREEN.FORGOT_EMAIL_TIMEOUT_ERROR} - ${ACTION.PAGE_VIEW}`;
const VIEW_FORGOT_EMAIL_MULTIPLE_ATTEMPTS_ERROR = `${SCREEN.FORGOT_EMAIL_MULTIPLE_ATTEMPTS_ERROR} - ${ACTION.PAGE_VIEW}`;
const CLICK_EVENT = `${SCREEN.FORGOT_EMAIL_ADDRESS} - ${ACTION.CLICK}`;

type EmailReminderPinCodeProps = {
  onError: (props: 'timeout' | 'notice' | 'authError' | null) => void;
  onSuccess: () => void;
  getRecaptchaTokenFunction: () => Promise<string>;
  reCaptchaPlatform: ReCaptchaPlatform;
  onVoiceCallInitiateSuccess: () => void;
  phoneNumber: string;
  otpSession: string | null;
};

const EmailReminderPinCode = (props: EmailReminderPinCodeProps) => {
  const formRef = useRef<View>(null);
  const codeInputRef = useRef<TextInput>(null);
  const otpIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reference:
  // https://github.com/paidy/api-consumers-rust/blob/master/docs/flows/emailReminder/respond_to_challenge.md
  const [
    respondToMyEmailReminderChallenge,
  ] = useRespondToMyEmailReminderChallengeMutation({
    context: {
      uri: process.env.CONSUMER_API_HOST_GRAPHQL_PUBLIC,
    },
  });

  // Reference:
  // https://github.com/paidy/api-consumers-rust/blob/master/docs/flows/emailReminder/initiate.md
  const [initiateMyEmailReminder] = useInitiateMyEmailReminderMutation({
    context: {
      uri: process.env.CONSUMER_API_HOST_GRAPHQL_PUBLIC,
    },
  });

  const [otpSession, setOtpSession] = useState(props.otpSession);
  const [showOTPError, setShowOTPError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const restartIdleTimer = () => {
    resetIdleTimer();

    otpIdleTimerRef.current = setTimeout(() => {
      Mixpanel.trackEvent(VIEW_FORGOT_EMAIL_TIMEOUT_ERROR);
      props.onError('timeout');
    }, OTP_IDLE_TIME_LIMIT);
  };

  const submitInitiateEmailReminder = throttle(
    async (method: PinCodeDispatchMethod) => {
      Mixpanel.trackEvent(CLICK_EVENT, {
        ITEM: ITEM.SUBMIT,
        'Event Type': method,
      });

      setErrorMessage('');

      try {
        const reCaptchaToken = await props.getRecaptchaTokenFunction();

        try {
          // API requires international standard format
          // e.g. 08000000001 -> +8000000001
          const phoneWithCountryPrefix = parsePhoneNumber(
            props.phoneNumber,
            'JP'
          ).number.toString();
          const result = await initiateMyEmailReminder({
            variables: {
              phoneNumber: phoneWithCountryPrefix,
              pinCodeDispatchMethod: method,
              recaptchaPlatform: props.reCaptchaPlatform,
              recaptchaToken: reCaptchaToken,
            },
          });

          // Expect to get a session value
          if (result.data) {
            setOtpSession(result.data?.initiateMyEmailReminder);
            props.onVoiceCallInitiateSuccess();
          } else {
            setErrorMessage('エラーが発生しました');
          }
          // Failed to initialize reminder flow
        } catch (e: any) {
          // BE also validate phone number format.
          if (e.graphQLErrors?.[0]?.message === 'wrong phone_number') {
            setErrorMessage(
              '携帯電話番号に誤りがあります。11桁で入力してください。'
            );
            // This phone number has already been used in the email reminder flow within 24h
          } else if (e.graphQLErrors?.[0]?.extensions?.code === 403) {
            Mixpanel.trackEvent(VIEW_FORGOT_EMAIL_MULTIPLE_ATTEMPTS_ERROR, {
              ITEM: ITEM.CLOSE_DIALOG,
            });

            props.onError('notice');

            // Usually something wrong with BE
          } else if (e.graphQLErrors?.[0]?.extensions?.code === 500) {
            setErrorMessage('サーバーエラーが発生しました');
          } else {
            setErrorMessage('エラーが発生しました');
          }
        }
        // Failed to get a recaptcha token.
      } catch (e) {
        setErrorMessage('エラーが発生しました');
      }
    },
    2000,
    { leading: true, trailing: false }
  );

  const resetIdleTimer = () => {
    if (otpIdleTimerRef.current) {
      clearTimeout(otpIdleTimerRef.current);
    }
  };

  const onFulFill = (code: string) => {
    restartIdleTimer();
    confirmOTP(code);
  };

  const confirmOTP = async (code: string) => {
    if (otpSession) {
      try {
        const reCaptchaToken = await props.getRecaptchaTokenFunction();

        setShowOTPError(false);

        try {
          const data = await respondToMyEmailReminderChallenge({
            variables: {
              session: otpSession,
              answer: code,
              recaptchaPlatform: props.reCaptchaPlatform,
              recaptchaToken: reCaptchaToken,
            },
          });
          const newSession = data.data?.respondToMyEmailReminderChallenge;

          // If `newSession` exists, it means the OTP challenge failed.
          // The user may try again using the new session value.
          if (newSession) {
            Mixpanel.trackEvent(ERROR_INVALID_OTP);
            setShowOTPError(true);
            setOtpSession(newSession);
            // Success
          } else {
            Mixpanel.trackEvent(VIEW_SUCCESS_PAGE, {
              Item: ITEM.CLOSE_DIALOG,
            });

            props.onSuccess();
          }
        } catch (err: any) {
          if (err.graphQLErrors?.[0]?.extensions?.code === 500) {
            setErrorMessage('サーバーエラーが発生しました');

            // OTP challenge failed more than 3 times.
            // The phoneNumber should be locked out for a while.
          } else if (err.graphQLErrors?.[0]?.extensions?.code === 401) {
            props.onError('authError');
          } else {
            setErrorMessage('エラーが発生しました');
          }
        }
      } catch (e) {
        setErrorMessage('エラーが発生しました');
      }
    } else {
      setErrorMessage('エラーが発生しました');
    }
  };

  useEffect(() => {
    // Automatically focus the input
    codeInputRef.current?.focus();
  }, []);

  return (
    <View>
      <View
        style={style.root}
        {...Platform.select({
          web: { accessibilityRole: 'form', ref: formRef },
        })}
      >
        <View>
          <Text style={[style.text, style.title]}>認証コード入力</Text>
          <Text style={[style.text, { fontSize: 14 }]}>
            SMSに送信された4桁の認証コードを入力してください。
          </Text>
          <View style={style.spacer} />
          <CodeInput
            onFulFill={onFulFill}
            style={style.codeField}
            errorMessage={errorMessage || '認証コードに誤りがあります'}
            showError={showOTPError}
            id="edit-otp-code-field"
            ref={codeInputRef}
          />
          <OTPVoiceCallContainer
            onVoiceCallInvoke={() => {
              submitInitiateEmailReminder(PinCodeDispatchMethod.VOICE);
            }}
          />
        </View>
      </View>
    </View>
  );
};

const style = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'space-between',
  },
  text: {
    marginBottom: 8,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  codeField: {
    width: 272,
    alignSelf: 'center',
  },
  spacer: {
    marginBottom: 48,
  },
});

export default EmailReminderPinCode;
